import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Dayjs } from 'dayjs';
import { useDashboardSettingsOptional } from '../../../contexts/DashboardSettingsContext';
import type { ComponentData, DateRange, ReportingComponentProps } from './BaseReportingComponent.types';
import {
  fetchStandardReportingComponent,
  parseReportingFetchError,
  tryCustomReportingFetcher,
} from './reportingComponentApi';

export interface BaseReportingHookProps
  extends Pick<
    ReportingComponentProps,
    | 'artifactId'
    | 'componentType'
    | 'initialDateRange'
    | 'includeProjections'
    | 'includeHistorical'
    | 'onDataLoaded'
    | 'onRefresh'
    | 'customDataFetcher'
  > {}

export interface BaseReportingComponentState {
  loading: boolean;
  error: string | null;
  data: ComponentData | null;
  dateRange: DateRange;
  showProjections: boolean;
}

/** Fetch + dashboard settings + range handlers; API split into `reportingComponentApi`. */
// eslint-disable-next-line max-lines-per-function -- remaining body is state wiring
export function useBaseReportingComponent({
  artifactId,
  componentType,
  initialDateRange,
  includeProjections = false,
  includeHistorical = true,
  onDataLoaded,
  onRefresh,
  customDataFetcher,
}: BaseReportingHookProps) {
  const dashboardSettings = useDashboardSettingsOptional();

  const [state, setState] = useState<BaseReportingComponentState>({
    loading: true,
    error: null,
    data: null,
    dateRange: initialDateRange || { startDate: null, endDate: null },
    showProjections: includeProjections,
  });

  const effectiveDateRange = useMemo(() => {
    if (
      dashboardSettings &&
      (dashboardSettings.dateRange.startDate || dashboardSettings.dateRange.endDate)
    ) {
      return dashboardSettings.dateRange;
    }
    return state.dateRange;
  }, [dashboardSettings, state.dateRange]);

  const effectiveShowProjections = dashboardSettings?.showProjections ?? state.showProjections;

  const fetchData = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    if (customDataFetcher) {
      const customData = await tryCustomReportingFetcher(customDataFetcher, artifactId);
      if (customData) {
        setState((prev) => ({
          ...prev,
          loading: false,
          data: customData,
          error: null,
        }));
        onDataLoaded?.(customData);
        return;
      }
    }

    try {
      const data = await fetchStandardReportingComponent(
        artifactId,
        componentType,
        effectiveDateRange,
        effectiveShowProjections,
        includeHistorical
      );

      setState((prev) => ({
        ...prev,
        loading: false,
        data,
        error: null,
      }));

      onDataLoaded?.(data);
    } catch (error: unknown) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: parseReportingFetchError(error),
      }));
    }
  }, [
    artifactId,
    componentType,
    effectiveDateRange,
    effectiveShowProjections,
    includeHistorical,
    onDataLoaded,
    customDataFetcher,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setState((prev) => ({
      ...prev,
      dateRange: {
        startDate: dates?.[0]?.toDate() || null,
        endDate: dates?.[1]?.toDate() || null,
      },
    }));
  };

  const handleProjectionToggle = (checked: boolean) => {
    setState((prev) => ({
      ...prev,
      showProjections: checked,
    }));
  };

  const handleRefresh = () => {
    fetchData();
    onRefresh?.();
  };

  return {
    state,
    effectiveDateRange,
    effectiveShowProjections,
    handleDateRangeChange,
    handleProjectionToggle,
    handleRefresh,
    fetchData,
  };
}
